// src/employee/pages/EmployeeDashboard.js
import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaTasks, FaUmbrellaBeach, FaClock, FaMoneyBillWave,
    FaCalendarAlt, FaUserCircle, FaBell, FaSignOutAlt, FaInbox, FaTimes, FaComments,
    FaLaptopCode, FaHandshake, FaChevronLeft, FaChevronRight
} from "react-icons/fa";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { useEmployeeData } from "../context/EmployeeDataContext";
import EmployeeChatbox from "./EmployeeChatbox";
import { updateDoc, doc, onSnapshot, collection } from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";


export default function EmployeeDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useEmployeeAuth();
    const { tasks, leaves, payouts, notifications } = useEmployeeData();
    const [showInbox, setShowInbox] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [apps, setApps] = useState([
        { id: 'customer-app', title: 'Customer App' },
        { id: 'partner-app', title: 'Partner App' }
    ]);

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "tech_apps"), (s) => {
            if (!s.empty) {
                setApps(s.docs.map(d => ({ id: d.id, ...d.data() })));
            }
        });
        return unsub;
    }, []);

    const stats = useMemo(() => {
        const pendingTasks = tasks.filter(t => t.status === "pending").length;
        const approvedLeaves = leaves.filter(l => l.status === "approved").length;
        const monthlyEarnings = payouts.reduce((s, p) => s + (p.finalPaid || 0), 0);
        const completedTasks = tasks.filter(t => t.status === "completed").length;

        return { pendingTasks, approvedLeaves, monthlyEarnings, completedTasks };
    }, [tasks, leaves, payouts]);

    // === REAL-TIME UNREAD CHAT COUNT FROM HR ===
    useEffect(() => {
        const hrEmail = "kanishka@driveeasy.co.in"; // CHANGE TO YOUR HR EMAIL
        const chatId = `${hrEmail}_${user.email}`;
        const chatRef = doc(db, "hrChats", chatId);

        const unsub = onSnapshot(chatRef, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                const unread = data.unread?.[user.email] === true ? 1 : 0;
                setChatUnreadCount(unread);
            } else {
                setChatUnreadCount(0);
            }
        });

        return unsub;
    }, [user.email]);

    useEffect(() => {
        if (chatUnreadCount > 0 && !showChat) {
            const audio = new Audio("/sounds/notification.mp3");  // ← Correct path
            audio.volume = 0.4;
            audio.play().catch(() => { }); // Ignore autoplay block
        }
    }, [chatUnreadCount, showChat]);

    const markAsRead = async (id) => {
        await updateDoc(doc(db, "employeeNotifications", id), { read: true });
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    const quickLinks = [
        { path: "tasks", icon: FaTasks, label: "Tasks", color: "from-blue-500 to-cyan-500" },
        { path: "leaves", icon: FaUmbrellaBeach, label: "Leaves", color: "from-emerald-500 to-teal-500" },
        { path: "payroll", icon: FaMoneyBillWave, label: "Payroll", color: "from-green-500 to-emerald-600" },
        { path: "calendar", icon: FaCalendarAlt, label: "Calendar", color: "from-indigo-500 to-purple-600" },
        { path: "profile", icon: FaUserCircle, label: "Profile", color: "from-pink-500 to-rose-500" },
    ];

    // Add Tech Access Links Dynamically
    apps.forEach(app => {
        const hasAccess = user?.role === 'hr' || user?.techAccess?.includes(app.id);
        if (hasAccess) {
            quickLinks.push({
                path: `tech/${app.id}`,
                icon: app.id === 'customer-app' ? FaLaptopCode : FaHandshake,
                label: app.title,
                color: app.id === 'customer-app' ? "from-orange-500 to-red-500" : "from-blue-500 to-indigo-500"
            });
        }
    });

    const currentPath = location.pathname.split("/").pop(); // e.g. "tasks"

    return (
        <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950 flex overflow-x-hidden">

            {/* ===== SIDEBAR (FIXED) ===== */}
            <motion.div
                initial={false}
                animate={{ width: isCollapsed ? 100 : 320 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed left-0 top-0 h-full backdrop-blur-3xl bg-black/40 border-r border-white/10 z-50 p-6 flex flex-col overflow-y-auto no-scrollbar"
            >
                {/* Logo */}
                <div className="mb-8 flex items-center justify-between min-h-[40px]">
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <h1 className="text-3xl font-black text-white">DriveEasy</h1>
                            <p className="text-white/60 text-xs">You Work. Your Way.</p>
                        </motion.div>
                    )}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all ${isCollapsed ? 'mx-auto' : ''}`}
                    >
                        {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </button>
                </div>

                {/* User Info */}
                <div className={`flex items-center gap-4 mb-8 p-3 bg-white/5 rounded-2xl ${isCollapsed ? 'justify-center' : ''}`}>
                    <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white font-black text-lg">
                        {user?.name?.[0] || "E"}
                    </div>
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <p className="text-white font-bold text-sm truncate max-w-[140px]">{user?.name || "Employee"}</p>
                            <p className="text-white/60 text-[10px]">{user?.designation || "Team Member"}</p>
                        </motion.div>
                    )}
                </div>

                {/* Quick Links */}
                <div className={`grid ${isCollapsed ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-8`}>
                    {quickLinks.map((link) => {
                        const isActive = location.pathname.includes(link.path);
                        return (
                            <motion.button
                                key={link.path}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate(link.path)}
                                className={`group relative p-3 rounded-2xl transition-all ${isActive
                                    ? "bg-white/10 border-2 border-cyan-400 shadow-cyan-500/30"
                                    : "bg-white/10 border border-white/20 hover:bg-white/15"
                                    }`}
                            >
                                <div className={`w-10 h-10 ${isCollapsed ? 'mx-auto' : 'mx-auto mb-2'} rounded-xl bg-gradient-to-r ${link.color} flex items-center justify-center transition-all`}>
                                    {React.createElement(link.icon, { className: "text-xl text-white" })}
                                </div>
                                {!isCollapsed && (
                                    <motion.p
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-white text-[10px] font-medium text-center truncate"
                                    >
                                        {link.label}
                                    </motion.p>
                                )}
                            </motion.button>
                        );
                    })}
                </div>

                {/* Inbox */}
                <button
                    onClick={() => setShowInbox(!showInbox)}
                    className={`flex items-center gap-3 p-4 bg-white/10 rounded-2xl hover:bg-white/15 transition mb-4 ${isCollapsed ? 'justify-center' : ''}`}
                >
                    <div className="relative">
                        <FaInbox className="text-white text-xl" />
                        {isCollapsed && unreadCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                                {unreadCount}
                            </span>
                        )}
                    </div>
                    {!isCollapsed && (
                        <div className="flex-1 text-left">
                            <p className="text-white font-medium text-sm">Inbox</p>
                            <p className="text-white/60 text-xs">{unreadCount} unread</p>
                        </div>
                    )}
                    {!isCollapsed && unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                            {unreadCount}
                        </span>
                    )}
                </button>

                {/* Chat with HR */}
                <button
                    onClick={() => setShowChat(!showChat)}
                    className={`flex items-center gap-3 p-4 bg-white/10 rounded-2xl hover:bg-white/15 transition w-full relative ${isCollapsed ? 'justify-center' : ''}`}
                >
                    <div className="relative">
                        <FaComments className="text-white text-xl" />
                        {isCollapsed && chatUnreadCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                                {chatUnreadCount}
                            </span>
                        )}
                    </div>
                    {!isCollapsed && (
                        <div className="flex-1 text-left">
                            <p className="text-white font-medium text-sm">Chat with HR</p>
                            <p className="text-white/60 text-xs">Direct message</p>
                        </div>
                    )}
                    {!isCollapsed && chatUnreadCount > 0 && (
                        <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse shadow-lg">
                            {chatUnreadCount}
                        </span>
                    )}
                </button>

                <div className="mt-auto">
                    {/* Logout */}
                    <button
                        onClick={logout}
                        className={`mt-4 p-4 bg-red-500/20 hover:bg-red-500/40 rounded-2xl text-red-300 transition flex items-center justify-center gap-2 w-full ${isCollapsed ? 'sm:p-3' : ''}`}
                    >
                        <FaSignOutAlt /> {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </motion.div>

            {/* ===== INBOX PANEL ===== */}
            <AnimatePresence>
                {showInbox && (
                    <motion.div
                        initial={{ x: 400 }}
                        animate={{ x: 0 }}
                        exit={{ x: 400 }}
                        className={`fixed top-0 h-full w-96 backdrop-blur-3xl bg-black/60 border-r border-white/20 z-40 p-6 overflow-y-auto ${isCollapsed ? 'left-[100px]' : 'left-[320px]'}`}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-white">Inbox</h3>
                            <button onClick={() => setShowInbox(false)} className="text-white text-3xl hover:text-red-400">
                                <FaTimes />
                            </button>
                        </div>
                        {notifications.length === 0 ? (
                            <p className="text-white/60 text-center mt-20">No notifications</p>
                        ) : (
                            notifications.map(n => (
                                <motion.div
                                    key={n.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    onClick={() => {
                                        markAsRead(n.id);
                                        navigate(`/employee/tasks/${n.taskId}`);
                                        setShowInbox(false);
                                    }}
                                    className={`p-4 mb-3 rounded-2xl border cursor-pointer transition-all ${n.read ? "bg-white/5 border-white/10" : "bg-white/10 border-cyan-500/50 shadow-lg"
                                        }`}
                                >
                                    <p className={`font-semibold ${n.read ? "text-white/70" : "text-white"}`}>{n.title}</p>
                                    <p className="text-white/60 text-sm">{n.message}</p>
                                    <p className="text-white/40 text-xs mt-1">
                                        {n.createdAt?.seconds ? format(new Date(n.createdAt.seconds * 1000), "MMM d, h:mm a") : "Just now"}
                                    </p>
                                </motion.div>
                            ))
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ===== CHAT PANEL ===== */}
            <AnimatePresence>
                {showChat && <EmployeeChatbox isOpen={showChat} onClose={() => setShowChat(false)} />}
            </AnimatePresence>

            {/* ===== MAIN CONTENT (RIGHT SIDE) ===== */}
            <motion.div
                layout
                animate={{ marginLeft: isCollapsed ? 100 : 320 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="flex-1"
            >
                <div className="h-screen overflow-y-auto">
                    <Outlet context={{ stats }} />
                </div>
            </motion.div>
        </div>
    );
}